// Un reporte de texto de ancho fijo (cartola con el formato de Random) como archivo PDF, armado en el
// navegador y sin dependencias: PDF 1.4 con Courier estándar (WinAnsi), como el escritor del DTE
// (packages/dte/src/representacion.ts). Existe porque el PC del mesón abre el ERP con impresión
// directa (--kiosk-printing): ahí imprimir nunca muestra «Guardar como PDF», y Nairobis necesita el
// archivo para mandárselo al cliente por WhatsApp (2026-11-02).
import type { LineaReporte } from '../logistica/reporteRandom';

const MM = 72 / 25.4;
/** Ancho de un carácter de Courier, en em. */
const COURIER = 0.6;

/** Texto PDF en WinAnsi: lo que no es latin1 se reemplaza; paréntesis y barras se escapan. */
function lit(s: string): string {
  const limpio = [...s.normalize('NFC')].map((c) => (c.charCodeAt(0) <= 255 ? c : '?')).join('');
  return `(${limpio.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)')})`;
}

/**
 * Las líneas en hojas A4 (apaisadas con `horizontal`), con lo mismo que el impreso: todo en negrita
 * salvo las de detalle, el título subrayado y el bloque centrado en la hoja. La letra se achica solo
 * si la línea más larga no cabe; lo que no entra en una hoja sigue en la siguiente.
 */
export function pdfDeReporte(
  lineas: LineaReporte[],
  { titulo, horizontal = false }: { titulo: string; horizontal?: boolean },
): Blob {
  const [ancho, alto] = horizontal ? [842, 595] : [595, 842];
  const margen = 10 * MM;
  const columnas = Math.max(1, ...lineas.map((l) => l.texto.length));
  const tam = Math.min(10, (ancho - 2 * margen) / (columnas * COURIER));
  const letra = tam * COURIER;
  const interlinea = tam * 1.35;
  const x0 = (ancho - columnas * letra) / 2;
  const porHoja = Math.max(1, Math.floor((alto - 2 * margen) / interlinea));

  const hojas: string[] = [];
  for (let i = 0; i < lineas.length || i === 0; i += porHoja) {
    const ops: string[] = [];
    lineas.slice(i, i + porHoja).forEach((l, j) => {
      if (!l.texto.trim()) return;
      const y = alto - margen - tam - j * interlinea;
      ops.push(
        `BT /${l.detalle ? 'F1' : 'F2'} ${tam.toFixed(2)} Tf ${x0.toFixed(2)} ${y.toFixed(2)} Td ${lit(l.texto)} Tj ET`,
      );
      if (l.titulo) {
        const desde = x0 + (l.texto.length - l.texto.trimStart().length) * letra;
        const hasta = x0 + l.texto.trimEnd().length * letra;
        const yl = (y - tam * 0.15).toFixed(2);
        ops.push(`0.6 w ${desde.toFixed(2)} ${yl} m ${hasta.toFixed(2)} ${yl} l S`);
      }
    });
    hojas.push(ops.join('\n'));
  }

  // Objetos: 1 catálogo, 2 páginas, 3 F1, 4 F2, luego página + contenido por hoja, y la ficha.
  const objetos: string[] = [];
  const primera = 5;
  objetos.push('<< /Type /Catalog /Pages 2 0 R >>');
  objetos.push(
    `<< /Type /Pages /Kids [${hojas.map((_, i) => `${primera + i * 2} 0 R`).join(' ')}] /Count ${hojas.length} >>`,
  );
  objetos.push('<< /Type /Font /Subtype /Type1 /BaseFont /Courier /Encoding /WinAnsiEncoding >>');
  objetos.push(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Courier-Bold /Encoding /WinAnsiEncoding >>',
  );
  hojas.forEach((contenido, i) => {
    objetos.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${ancho} ${alto}] ` +
        `/Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${primera + i * 2 + 1} 0 R >>`,
    );
    objetos.push(`<< /Length ${contenido.length} >>\nstream\n${contenido}\nendstream`);
  });
  objetos.push(`<< /Title ${lit(titulo)} /Producer (MADECI ERP) >>`);

  // Todo es latin1 (lit ya lo dejó así): un carácter = un byte, y las posiciones salen del largo.
  let pdf = '%PDF-1.4\n%\xe2\xe3\xcf\xd3\n';
  const posiciones = objetos.map((o, i) => {
    const pos = pdf.length;
    pdf += `${i + 1} 0 obj\n${o}\nendobj\n`;
    return pos;
  });
  const xref = pdf.length;
  pdf +=
    `xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n` +
    posiciones.map((p) => `${String(p).padStart(10, '0')} 00000 n \n`).join('') +
    `trailer\n<< /Size ${objetos.length + 1} /Root 1 0 R /Info ${objetos.length} 0 R >>\n` +
    `startxref\n${xref}\n%%EOF\n`;
  return new Blob([Uint8Array.from(pdf, (c) => c.charCodeAt(0))], { type: 'application/pdf' });
}
